import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Settings as SettingsIcon, KeyRound, CheckCircle2, XCircle, Save, Sparkles } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(null);
  const [provider, setProvider] = useState("deepgram");
  const [deepgramKey, setDeepgramKey] = useState("");
  const [googleJson, setGoogleJson] = useState("");
  const [googleLocation, setGoogleLocation] = useState("us-central1");
  const [googleLanguage, setGoogleLanguage] = useState("pt-BR");
  const [llmProvider, setLlmProvider] = useState("emergent");
  const [anthropicKey, setAnthropicKey] = useState("");
  const [openaiKey, setOpenaiKey] = useState("");
  const [llmModel, setLlmModel] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/settings")
      .then((r) => {
        setCurrent(r.data);
        setProvider(r.data.stt_provider || "deepgram");
        setGoogleLocation(r.data.google_location || "us-central1");
        setGoogleLanguage(r.data.google_language || "pt-BR");
        setLlmProvider(r.data.llm_provider || r.data.llm_provider_resolved || "emergent");
        setLlmModel(r.data.llm_model || "");
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha a carregar definições"))
      .finally(() => setLoading(false));
  }, [user]);

  if (user?.role !== "admin") {
    return <Navigate to="/copilot" replace />;
  }

  async function save(partial) {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", partial);
      setCurrent(data);
      toast.success("Definições atualizadas");
      setDeepgramKey("");
      setGoogleJson("");
      setAnthropicKey("");
      setOpenaiKey("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao atualizar");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-[#64748B] p-6">A carregar…</div>;

  async function testConnection() {
    if (llmProvider !== "anthropic" && llmProvider !== "openai") {
      toast.error("Escolha Anthropic ou OpenAI para testar.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const payload = { provider: llmProvider, model: llmModel || undefined };
      const typed = llmProvider === "anthropic" ? anthropicKey.trim() : openaiKey.trim();
      if (typed) payload.api_key = typed;
      const { data } = await api.post("/admin/test-llm", payload);
      setTestResult(data);
      if (data.ok) toast.success(data.message || "Ligação bem-sucedida");
      else toast.error(data.error || "Falha na ligação");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Falha ao testar";
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="rounded-2xl border border-white/10 bg-[#111827] p-6">
        <div className="flex items-center gap-2 mb-1">
          <SettingsIcon className="w-5 h-5 text-[#2563EB]" />
          <h2 className="font-display font-extrabold text-xl">Definições de administração</h2>
        </div>
        <p className="text-sm text-[#94A3B8]">
          Fornecedor de IA de texto e chaves de transcrição. Aplicam-se a toda a plataforma. Ficam gravadas em base de dados e sobrepõem-se às variáveis de ambiente — ideal para instalações próprias (VPS).
        </p>
      </div>

      {/* IA de texto (LLM) */}
      <div className="rounded-2xl border border-white/10 bg-[#111827] p-6 space-y-4" data-testid="admin-llm-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#8B5CF6]" />
            <div>
              <h3 className="font-display font-bold text-base">IA de texto (respostas, deteção, coaching)</h3>
              <p className="text-sm text-[#94A3B8] mt-1">Escolha o fornecedor e cole a sua própria API Key. Guardada em base de dados.</p>
            </div>
          </div>
          <Badge className="bg-white/5 text-[#c4b5fd] border-white/10">
            ativo: {current?.llm_provider_resolved || "emergent"}
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { id: "anthropic", title: "Anthropic Claude", desc: "Sonnet — recomendado para PT-PT." },
            { id: "openai", title: "OpenAI GPT", desc: "GPT-4o e superiores." },
            { id: "emergent", title: "Emergent (dev)", desc: "Chave universal. Só em desenvolvimento.", disabled: !current?.emergent_available },
          ].map((opt) => (
            <label
              key={opt.id}
              className={`rounded-xl border p-4 transition-all ${opt.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"} ${llmProvider === opt.id ? "border-[#8B5CF6] bg-[#8B5CF6]/10" : "border-white/10 hover:border-white/25"}`}
            >
              <input
                type="radio" name="llmProvider" value={opt.id} className="sr-only"
                checked={llmProvider === opt.id}
                disabled={opt.disabled}
                onChange={() => setLlmProvider(opt.id)}
                data-testid={`admin-llm-${opt.id}`}
              />
              <div className="font-semibold text-sm">{opt.title}</div>
              <div className="text-xs text-[#94A3B8] mt-1">{opt.desc}</div>
            </label>
          ))}
        </div>

        {llmProvider === "anthropic" && (
          <div className="space-y-2" data-testid="admin-anthropic-block">
            <div className="flex items-center justify-between">
              <label className="text-xs text-[#64748B] flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Anthropic API Key</label>
              {current?.anthropic_api_key_set
                ? <span className="flex items-center gap-1 text-xs text-[#10B981]"><CheckCircle2 className="w-3.5 h-3.5"/>configurada {current?.anthropic_api_key && <span className="font-mono text-[#64748B] ml-1">({current.anthropic_api_key})</span>}</span>
                : <span className="flex items-center gap-1 text-xs text-[#EF4444]"><XCircle className="w-3.5 h-3.5"/>em falta</span>}
            </div>
            <Input
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
              placeholder="sk-ant-… (deixe vazio para manter a atual)"
              className="bg-[#0B0F17] border-white/10 font-mono text-sm"
              data-testid="admin-anthropic-key-input"
            />
          </div>
        )}

        {llmProvider === "openai" && (
          <div className="space-y-2" data-testid="admin-openai-block">
            <div className="flex items-center justify-between">
              <label className="text-xs text-[#64748B] flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> OpenAI API Key</label>
              {current?.openai_api_key_set
                ? <span className="flex items-center gap-1 text-xs text-[#10B981]"><CheckCircle2 className="w-3.5 h-3.5"/>configurada {current?.openai_api_key && <span className="font-mono text-[#64748B] ml-1">({current.openai_api_key})</span>}</span>
                : <span className="flex items-center gap-1 text-xs text-[#EF4444]"><XCircle className="w-3.5 h-3.5"/>em falta</span>}
            </div>
            <Input
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
              placeholder="sk-… (deixe vazio para manter a atual). Também usada para transcrição Whisper."
              className="bg-[#0B0F17] border-white/10 font-mono text-sm"
              data-testid="admin-openai-key-input"
            />
          </div>
        )}

        {(llmProvider === "anthropic" || llmProvider === "openai") && (
          <div>
            <label className="text-xs text-[#64748B] mb-1 block">Modelo (opcional — deixe vazio para usar o padrão)</label>
            <Input
              value={llmModel}
              onChange={(e) => setLlmModel(e.target.value)}
              placeholder={current?.llm_model_defaults?.[llmProvider] || ""}
              className="bg-[#0B0F17] border-white/10 font-mono text-sm"
              data-testid="admin-llm-model-input"
            />
            <p className="text-[11px] text-[#64748B] mt-1">Padrão: <span className="font-mono">{current?.llm_model_defaults?.[llmProvider]}</span>. Ajuste se a sua conta usar outro ID de modelo.</p>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={testConnection}
            disabled={testing || (llmProvider !== "anthropic" && llmProvider !== "openai")}
            variant="outline"
            className="border-[#8B5CF6]/40 bg-transparent text-[#c4b5fd] hover:bg-[#8B5CF6]/10"
            data-testid="admin-test-llm"
          >
            {testing ? "A testar…" : "Testar ligação"}
          </Button>
          <Button
            onClick={() => {
              const payload = { llm_provider: llmProvider, llm_model: llmModel };
              if (llmProvider === "anthropic" && anthropicKey.trim()) payload.anthropic_api_key = anthropicKey.trim();
              if (llmProvider === "openai" && openaiKey.trim()) payload.openai_api_key = openaiKey.trim();
              save(payload);
            }}
            disabled={saving}
            className="bg-[#8B5CF6] hover:bg-[#7C3AED]"
            data-testid="admin-save-llm"
          >
            <Save className="w-4 h-4 mr-1.5" /> Guardar IA de texto
          </Button>
          {llmProvider === "anthropic" && (
            <Button onClick={() => save({ anthropic_api_key: "" })} variant="outline" disabled={saving} className="border-white/10 bg-transparent text-[#94A3B8]" data-testid="admin-clear-anthropic">
              Limpar chave Anthropic
            </Button>
          )}
          {llmProvider === "openai" && (
            <Button onClick={() => save({ openai_api_key: "" })} variant="outline" disabled={saving} className="border-white/10 bg-transparent text-[#94A3B8]" data-testid="admin-clear-openai">
              Limpar chave OpenAI
            </Button>
          )}
        </div>
        {testResult && (
          <div
            className={`rounded-xl border p-3 text-sm flex items-start gap-2 ${testResult.ok ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#a7f3d0]" : "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#fecaca]"}`}
            data-testid="admin-test-llm-result"
          >
            {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>{testResult.ok ? (testResult.message || "Ligação bem-sucedida.") : (testResult.error || "Falha na ligação.")}</span>
          </div>
        )}
      </div>

      {/* Provedor ativo */}
      <div className="rounded-2xl border border-white/10 bg-[#111827] p-6 space-y-4" data-testid="admin-provider-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-base">Provedor de STT ativo</h3>
            <p className="text-sm text-[#94A3B8] mt-1">Quem transcreve o áudio das chamadas em direto.</p>
          </div>
          <Badge className="bg-white/5 text-[#93b4fb] border-white/10">
            atual: {current?.stt_provider || "deepgram"}
          </Badge>
        </div>
        <div className="flex gap-3">
          <label className={`flex-1 rounded-xl border p-4 cursor-pointer transition-all ${provider === "deepgram" ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/10 hover:border-white/25"}`}>
            <input
              type="radio" name="provider" value="deepgram"
              className="sr-only"
              checked={provider === "deepgram"}
              onChange={() => setProvider("deepgram")}
              data-testid="admin-provider-deepgram"
            />
            <div className="font-semibold">Deepgram Nova-3</div>
            <div className="text-xs text-[#94A3B8] mt-1">WebSocket, ~250 ms, pt-PT/pt-BR/multi, diarização.</div>
          </label>
          <label className={`flex-1 rounded-xl border p-4 cursor-pointer transition-all ${provider === "google" ? "border-[#2563EB] bg-[#2563EB]/10" : "border-white/10 hover:border-white/25"}`}>
            <input
              type="radio" name="provider" value="google"
              className="sr-only"
              checked={provider === "google"}
              onChange={() => setProvider("google")}
              data-testid="admin-provider-google"
            />
            <div className="font-semibold">Google Speech V2 (chirp_2)</div>
            <div className="text-xs text-[#94A3B8] mt-1">gRPC streaming, pt-BR, ~500 ms. Sem diarização em streaming.</div>
          </label>
        </div>
        <Button
          onClick={() => save({ stt_provider: provider })}
          disabled={saving || provider === current?.stt_provider}
          className="bg-[#2563EB] hover:bg-[#1D4ED8]"
          data-testid="admin-save-provider"
        >
          <Save className="w-4 h-4 mr-1.5" /> Guardar provedor
        </Button>
      </div>

      {/* Deepgram */}
      <div className="rounded-2xl border border-white/10 bg-[#111827] p-6 space-y-4" data-testid="admin-deepgram-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#10B981]" />
            <h3 className="font-display font-bold text-base">Deepgram — API Key</h3>
          </div>
          {current?.deepgram_api_key_set ? (
            <span className="flex items-center gap-1 text-xs text-[#10B981]"><CheckCircle2 className="w-4 h-4"/>configurada</span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-[#EF4444]"><XCircle className="w-4 h-4"/>em falta</span>
          )}
        </div>
        {current?.deepgram_api_key && (
          <div className="text-xs text-[#64748B]">Atual (parcial): <span className="font-mono">{current.deepgram_api_key}</span></div>
        )}
        <Input
          value={deepgramKey}
          onChange={(e) => setDeepgramKey(e.target.value)}
          placeholder="Colar nova chave Deepgram (deixe vazio para manter)"
          className="bg-[#0B0F17] border-white/10 font-mono text-sm"
          data-testid="admin-deepgram-key-input"
        />
        <div className="flex gap-2">
          <Button
            onClick={() => save({ deepgram_api_key: deepgramKey })}
            disabled={saving || !deepgramKey.trim()}
            className="bg-[#10B981] hover:bg-[#059669]"
            data-testid="admin-save-deepgram"
          >
            <Save className="w-4 h-4 mr-1.5" /> Guardar Deepgram
          </Button>
          <Button
            onClick={() => save({ deepgram_api_key: "" })}
            variant="outline"
            disabled={saving}
            className="border-white/10 bg-transparent text-[#94A3B8]"
            data-testid="admin-clear-deepgram"
          >
            Limpar (usar ENV)
          </Button>
        </div>
      </div>

      {/* Google */}
      <div className="rounded-2xl border border-white/10 bg-[#111827] p-6 space-y-4" data-testid="admin-google-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#F59E0B]" />
            <h3 className="font-display font-bold text-base">Google STT — Credenciais</h3>
          </div>
          {current?.google_credentials_json_set ? (
            <span className="flex items-center gap-1 text-xs text-[#10B981]"><CheckCircle2 className="w-4 h-4"/>configuradas</span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-[#EF4444]"><XCircle className="w-4 h-4"/>em falta</span>
          )}
        </div>
        <p className="text-xs text-[#94A3B8]">
          Cole o JSON completo da <b>Service Account</b> com o papel <span className="font-mono">roles/speech.client</span>. O <span className="font-mono">project_id</span> é lido daqui — não precisa de configurar mais nada.
        </p>
        <Textarea
          value={googleJson}
          onChange={(e) => setGoogleJson(e.target.value)}
          placeholder='{ "type": "service_account", "project_id": "…", "client_email": "…", "private_key": "…" }'
          className="min-h-[180px] bg-[#0B0F17] border-white/10 font-mono text-xs"
          data-testid="admin-google-json-input"
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#64748B] mb-1 block">Região</label>
            <Input
              value={googleLocation}
              onChange={(e) => setGoogleLocation(e.target.value)}
              placeholder="us-central1"
              className="bg-[#0B0F17] border-white/10"
              data-testid="admin-google-location"
            />
          </div>
          <div>
            <label className="text-xs text-[#64748B] mb-1 block">Idioma (chirp_2)</label>
            <Input
              value={googleLanguage}
              onChange={(e) => setGoogleLanguage(e.target.value)}
              placeholder="pt-BR"
              className="bg-[#0B0F17] border-white/10"
              data-testid="admin-google-language"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => save({
              google_credentials_json: googleJson,
              google_location: googleLocation,
              google_language: googleLanguage,
            })}
            disabled={saving || (!googleJson.trim() && googleLocation === (current?.google_location || "us-central1") && googleLanguage === (current?.google_language || "pt-BR"))}
            className="bg-[#F59E0B] hover:bg-[#D97706] text-[#111827]"
            data-testid="admin-save-google"
          >
            <Save className="w-4 h-4 mr-1.5" /> Guardar Google
          </Button>
          <Button
            onClick={() => save({ google_credentials_json: "" })}
            variant="outline"
            disabled={saving}
            className="border-white/10 bg-transparent text-[#94A3B8]"
            data-testid="admin-clear-google"
          >
            Limpar credenciais
          </Button>
        </div>
      </div>
    </div>
  );
}
